import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, FileText, Eye, CheckCircle, Copy, AlertCircle } from 'lucide-react';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { voucherService } from '../../../../../../services/vouchers/voucherService';
import { getAssetUrl, getProxyUrl } from '../../../../../../utils/assetUtils';
import { predefineItineraryService } from '../../../../../../services/predefineItinerary/predefineItineraryService';
import { useAgent } from "../../../../../../context/agent/AgentContext";
import { useSubscription } from "../../../../../../context/subscription/SubscriptionContext";
import { useDashboard } from "../../../../../../context/dashboard/DashboardContext";
import ConfirmationModal from '../../../../../../components/modals/ConfirmationModal';
import { toast } from 'react-hot-toast';
import { standardizeDate, standardizeTime } from '../../../../../../utils/voucherHelpers';
import { useVoucherLock } from '../../../../../../components/gating/PlanGate';

// Shared & Parent Sub-components
import PDFVoucherView from '../../../Shared/PDFVoucherView';
import EnrichVoucherModal from './EnrichVoucherModal';
import AiAnalysisProgress from '../AiVoucher/AiAnalysisProgress';

// Section Sub-components
import EnrichmentHeader from './sections/EnrichmentHeader';
import GuestInfoSection from './sections/GuestInfoSection';
import HotelBookingsSection from './sections/HotelBookingsSection';
import TravelBookingsSection from './sections/TravelBookingsSection';
import ItinerarySection from './sections/ItinerarySection';
import ContactDetailsSection from './sections/ContactDetailsSection';
import ImportantDocsSection from './sections/ImportantDocsSection';
import PartnerVisibilitySection from './sections/PartnerVisibilitySection';
import ClientSharingSection from './sections/ClientSharingSection';

const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
};

const formatToStandardDate = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const day = date.getDate();
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
};

const parseStandardDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  let cleanDate = dateStr.trim();
  cleanDate = cleanDate.replace(/^(mon|tue|wed|thu|fri|sat|sun)[a-z]*\s+/i, "");
  if (!cleanDate) return null;

  const isoRegex = /^(\d{4})-(\d{2})-(\d{2})/;
  const isoMatch = cleanDate.match(isoRegex);
  if (isoMatch) {
    return new Date(parseInt(isoMatch[1], 10), parseInt(isoMatch[2], 10) - 1, parseInt(isoMatch[3], 10));
  }

  const dmyRegex = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
  const dmyMatch = cleanDate.match(dmyRegex);
  if (dmyMatch) {
    return new Date(parseInt(dmyMatch[3], 10), parseInt(dmyMatch[2], 10) - 1, parseInt(dmyMatch[1], 10));
  }

  const parts = cleanDate.split(/[\s\-\,]+/);
  if (parts.length >= 2) {
    const day = parseInt(parts[0].replace(/\D/g, ""), 10);
    const monthStr = parts[1].toLowerCase().substring(0, 3);
    const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const monthIndex = months.indexOf(monthStr);

    if (monthIndex !== -1 && !isNaN(day)) {
      let year = new Date().getFullYear();
      if (parts[2] && /^\d{4}$/.test(parts[2])) {
        year = parseInt(parts[2], 10);
      }
      return new Date(year, monthIndex, day);
    }
  }

  const parsed = new Date(cleanDate);
  if (parsed && !isNaN(parsed.getTime())) {
    return parsed;
  }
  return null;
};

const calculateItineraryDate = (startDateStr, dayIndex) => {
  if (!startDateStr) return "";
  const startDate = parseStandardDate(startDateStr);
  if (!startDate) return "";
  const targetDate = new Date(startDate);
  targetDate.setDate(targetDate.getDate() + dayIndex);
  return formatToStandardDate(targetDate);
};

const ManualVoucherForm = ({
  initialData,
  setActiveTab,
  onDataChange,
  customTheme,
  onSaved,
  isEditMode = false,
  isReviewMode = false,
  voucherId = null
}) => {
  const navigate = useNavigate();
  const { isLocked: isVoucherLocked, limitReached } = useVoucherLock();
  const { agent, voucherTheme } = useAgent();
  const { refreshSubscription } = useSubscription();
  const { analysisState, resetAnalysis, setAnalysisMinimized } = useDashboard();

  const [isLoadingData, setIsLoadingData] = useState(Boolean(isEditMode && voucherId));
  const [loadError, setLoadError] = useState(null);
  const [loadedVoucherDbId, setLoadedVoucherDbId] = useState(null);
  const [isEnrichModalOpen, setIsEnrichModalOpen] = useState(false);
  const [formVoucherTheme, setFormVoucherTheme] = useState(() => {
    const t = customTheme || voucherTheme;
    if (t) {
      return {
        is_custom: Boolean(t.is_custom || t.isCustom),
        primary_color: t.primary_color || t.primaryColor || '#0d9488',
        secondary_color: t.secondary_color || t.secondaryColor || '#1d4ed8',
        text_color: t.text_color || t.textColor || '#111827',
        header_bg_color: t.header_bg_color || t.headerBgColor || '#ffffff',
        include_itinerary: Boolean(t.include_itinerary)
      };
    }
    return { is_custom: false, primary_color: '#0d9488', secondary_color: '#1d4ed8', text_color: '#111827', header_bg_color: '#ffffff', include_itinerary: false };
  });

  useEffect(() => {
    const t = customTheme || voucherTheme;
    if (t) {
      setFormVoucherTheme({
        is_custom: Boolean(t.is_custom || t.isCustom),
        primary_color: t.primary_color || t.primaryColor || '#0d9488',
        secondary_color: t.secondary_color || t.secondaryColor || '#1d4ed8',
        text_color: t.text_color || t.textColor || '#111827',
        header_bg_color: t.header_bg_color || t.headerBgColor || '#ffffff',
        include_itinerary: Boolean(t.include_itinerary)
      });
    }
  }, [customTheme, voucherTheme]);

  const lastEnrichResultRef = useRef(null);
  // Handle Enrichment Handoff (Even when minimized)
  useEffect(() => {
    if (analysisState.result && analysisState.analysisType === 'enrich' && !analysisState.isAnalyzing) {
      if (lastEnrichResultRef.current !== analysisState.result) {
        lastEnrichResultRef.current = analysisState.result;
        setFormData(analysisState.result);
        toast.success('Voucher enriched successfully!', { icon: '✨' });
        setIsEnrichModalOpen(false);
        // Reset analysis after consumption
        setTimeout(() => resetAnalysis(), 500);
      }
    }
  }, [analysisState.result, analysisState.analysisType, analysisState.isAnalyzing, resetAnalysis]);

  const [formData, setFormData] = useState(() => {
    const saved = !isEditMode ? localStorage.getItem('trrip_voucher_draft') : null;
    let parsedSaved = null;
    try {
      if (saved) parsedSaved = JSON.parse(saved);
    } catch (e) {
      console.error(e);
    }
    const base = initialData || parsedSaved || {};
    const defaultTodayDate = formatToStandardDate(new Date());
    return {
      guest_information: base.guest_information || {
        guest_name: '',
        total_guests_adults: '',
        total_guests_children: '',
        total_rooms_booked: ''
      },
      tour_info: base.tour_info ? {
        ...base.tour_info,
        start_date: base.tour_info.start_date || defaultTodayDate,
        end_date: base.tour_info.end_date || '',
        destinations: base.tour_info.destinations || []
      } : {
        start_date: defaultTodayDate,
        end_date: '',
        destinations: []
      },
      hotel_bookings: (base.hotel_bookings && base.hotel_bookings.length > 0)
        ? base.hotel_bookings.map(h => ({
          ...h,
          id: h.id || Math.random().toString(36).substr(2, 9),
          hotel_address: h.hotel_address || '',
          hotel_phone: h.hotel_phone || '',
          map_link: h.map_link || '',
          inclusions: h.inclusions || '',
          check_in_time: h.check_in_time || '',
          check_out_time: h.check_out_time || ''
        }))
        : [{
          id: Math.random().toString(36).substr(2, 9),
          hotel_name: '',
          hotel_address: '',
          hotel_phone: '',
          map_link: '',
          city: '',
          location_id: '',
          confirmation_number: '',
          room_category: '',
          check_in_date: '',
          check_in_time: '',
          check_out_date: '',
          check_out_time: '',
          no_of_nights: '',
          no_of_rooms: '',
          meal_plan: '',
          inclusions: ''
        }],
      travel_bookings: (base.travel_bookings || []).map(t => ({ ...t, id: t.id || Math.random().toString(36).substr(2, 9) })),
      contact_details: base.contact_details || [],
      itinerary: base.itinerary || [],
      important_notes: base.important_notes || [],
      important_documents: (base.important_documents || []).map(d => ({ ...d, id: d.id || Math.random().toString(36).substr(2, 9) })),
      source_documents: base.source_documents || initialData?.source_documents || [],
      hide_partners: base.hide_partners || false,
      partner_visibility: base.partner_visibility || {
        restaurant: true,
        activity: true,
        shop: true,
        show_platform_partners: true,
        show_agent_partners: true
      }
    };
  });

  // Auto-save form draft whenever user updates fields (only in create mode)
  useEffect(() => {
    if (formData && !isEditMode) {
      localStorage.setItem('trrip_voucher_draft', JSON.stringify(formData));
    }
  }, [formData, isEditMode]);

  const [bookingId, setBookingId] = useState(() => initialData?.booking_id || initialData?.bookingId || (!isEditMode ? localStorage.getItem('trrip_voucher_booking_id') : '') || '');

  const [logo, setLogo] = useState('');
  const [agentName, setAgentName] = useState('');
  const [agentEmail, setAgentEmail] = useState('');
  const [agentPhone, setAgentPhone] = useState('');
  const [agentAddress, setAgentAddress] = useState('');
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');

  const [isDownloading, setIsDownloading] = useState(false);
  const isSubmittingRef = useRef(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [generatedPdfUrl, setGeneratedPdfUrl] = useState("");
  const [showLimitPopup, setShowLimitPopup] = useState(false);
  const [allLocations, setAllLocations] = useState([]);
  const [isSaved, setIsSaved] = useState(isEditMode);
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const [selectiveExpanded, setSelectiveExpanded] = useState(false);

  // Edit Mode: Fetch Voucher and Locations data
  useEffect(() => {
    if (isEditMode && voucherId) {
      const fetchVoucherData = async () => {
        try {
          setIsLoadingData(true);
          setLoadError(null);
          const [voucherResponse, locationsResponse] = await Promise.all([
            voucherService.getVoucherById(voucherId),
            voucherService.getLocations()
          ]);
          const voucher = voucherResponse?.voucher || voucherResponse;
          if (!voucher || (!voucher._id && !voucher.booking_id)) {
            throw new Error("Voucher not found");
          }

          if (voucher._id) {
            setLoadedVoucherDbId(voucher._id);
          }

          const locations = locationsResponse?.data || locationsResponse || [];
          const sortedLocations = [...locations].sort((a, b) => a.name.localeCompare(b.name));
          setAllLocations(sortedLocations);

          const hotelBookings = (voucher.hotel_bookings || []).map((hotel) => {
            let locId = hotel.location_id?._id || hotel.location_id;
            let cityName = (hotel.city || '').trim();
            if (!locId && cityName) {
              const found = locations.find(l => l.name?.toLowerCase().trim() === cityName.toLowerCase());
              if (found) locId = found._id;
            }
            if (!locId && hotel.hotel_name) {
              const found = locations.find(l => hotel.hotel_name.toLowerCase().includes(l.name.toLowerCase()));
              if (found) {
                locId = found._id;
                if (!cityName) cityName = found.name;
              }
            }
            return {
              ...hotel,
              city: cityName || hotel.city || '',
              id: hotel.id || hotel._id || Math.random().toString(36).substr(2, 9),
              location_id: locId || '',
              check_in_date: standardizeDate(hotel.check_in_date),
              check_in_time: standardizeTime(hotel.check_in_time) || '',
              check_out_date: standardizeDate(hotel.check_out_date),
              check_out_time: standardizeTime(hotel.check_out_time) || ''
            };
          });

          // Authoritatively derive destinations from hotel bookings if valid hotels exist
          let loadedDestinations = voucher.tour_info?.destinations || [];
          const validLoadedHotels = hotelBookings.filter(h => h && ((h.city && h.city.trim()) || h.location_id || (h.hotel_name && h.hotel_name.trim())));
          if (validLoadedHotels.length > 0) {
            const derived = [];
            const seen = new Set();
            for (const h of validLoadedHotels) {
              let hLocId = h.location_id || null;
              let hCity = (h.city || '').trim();
              let hLocName = null;
              if (hLocId) {
                const found = locations.find(l => l._id?.toString() === hLocId.toString());
                if (found) {
                  hLocName = found.name;
                  if (!hCity) hCity = found.name;
                }
              }
              if (!hLocId && hCity) {
                const found = locations.find(l => l.name?.toLowerCase().trim() === hCity.toLowerCase());
                if (found) {
                  hLocId = found._id;
                  hLocName = found.name;
                }
              }
              const dedupKey = hLocId ? `loc_${hLocId}` : (hCity ? `city_${hCity.toLowerCase()}` : null);
              if (dedupKey && !seen.has(dedupKey)) {
                seen.add(dedupKey);
                derived.push({
                  city: hCity || hLocName || '',
                  location_id: hLocId || '',
                  location_name: hLocName || hCity || '',
                  journey_date: h.check_in_date || ''
                });
              }
            }
            if (derived.length > 0) {
              loadedDestinations = derived;
            }
          }

          setFormData({
            guest_information: voucher.guest_information || {
              guest_name: '',
              total_guests_adults: '',
              total_guests_children: '',
              total_rooms_booked: ''
            },
            hotel_bookings: hotelBookings.length > 0 ? hotelBookings : [{
              id: Math.random().toString(36).substr(2, 9),
              hotel_name: '',
              hotel_address: '',
              hotel_phone: '',
              map_link: '',
              city: '',
              location_id: '',
              confirmation_number: '',
              room_category: '',
              check_in_date: '',
              check_in_time: '',
              check_out_date: '',
              check_out_time: '',
              no_of_nights: '',
              no_of_rooms: '',
              meal_plan: '',
              inclusions: ''
            }],
            travel_bookings: (voucher.travel_bookings || []).map(t => ({
              ...t,
              id: t.id || t._id || Math.random().toString(36).substr(2, 9),
              journey_date: standardizeDate(t.journey_date),
              departure_time: standardizeTime(t.departure_time),
              arrival_time: standardizeTime(t.arrival_time)
            })),
            tour_info: voucher.tour_info ? {
              ...voucher.tour_info,
              start_date: standardizeDate(voucher.tour_info.start_date),
              end_date: standardizeDate(voucher.tour_info.end_date),
              destinations: loadedDestinations
            } : { start_date: '', end_date: '', destinations: [] },
            important_notes: voucher.important_notes || [],
            important_documents: (voucher.important_documents || []).map(d => ({ ...d, id: d.id || d._id || Math.random().toString(36).substr(2, 9) })),
            source_documents: voucher.source_documents || [],
            itinerary: (voucher.itinerary || []).map(item => ({ ...item, id: item.id || item._id || Math.random().toString(36).substr(2, 9) })),
            contact_details: (voucher.contact_details || []).map(c => ({ ...c, id: c.id || c._id || Math.random().toString(36).substr(2, 9) })),
            preferred_language: voucher.preferred_language || 'English',
            hide_partners: voucher.hide_partners || false,
            partner_visibility: voucher.partner_visibility || {
              restaurant: true,
              activity: true,
              shop: true,
              show_platform_partners: true,
              show_agent_partners: true
            }
          });

          if (voucher.booking_id) {
            setBookingId(voucher.booking_id);
          }

          if (customTheme) {
            setFormVoucherTheme({
              is_custom: Boolean(customTheme.is_custom || customTheme.isCustom),
              primary_color: customTheme.primary_color || customTheme.primaryColor || '#0d9488',
              secondary_color: customTheme.secondary_color || customTheme.secondaryColor || '#1d4ed8',
              text_color: customTheme.text_color || customTheme.textColor || '#111827',
              header_bg_color: customTheme.header_bg_color || customTheme.headerBgColor || '#ffffff',
              include_itinerary: Boolean(customTheme.include_itinerary)
            });
          } else if (voucher.voucher_theme && (voucher.voucher_theme.is_custom !== undefined || voucher.voucher_theme.isCustom !== undefined)) {
            setFormVoucherTheme({
              is_custom: Boolean(voucher.voucher_theme.is_custom || voucher.voucher_theme.isCustom),
              primary_color: voucher.voucher_theme.primary_color || voucher.voucher_theme.primaryColor || '#0d9488',
              secondary_color: voucher.voucher_theme.secondary_color || voucher.voucher_theme.secondaryColor || '#1d4ed8',
              text_color: voucher.voucher_theme.text_color || voucher.voucher_theme.textColor || '#111827',
              header_bg_color: voucher.voucher_theme.header_bg_color || voucher.voucher_theme.headerBgColor || '#ffffff',
              include_itinerary: Boolean(voucher.voucher_theme.include_itinerary)
            });
          }

          if (voucher.itenary_link) {
            const finalUrl = getAssetUrl(voucher.itenary_link);
            setGeneratedPdfUrl(finalUrl);
          }

          setIsSaved(true);
        } catch (error) {
          console.error("Error fetching voucher data for edit:", error);
          setLoadError("Unable to open voucher. Please return to the voucher list and try again.");
          toast.error("Failed to load voucher data");
        } finally {
          setIsLoadingData(false);
        }
      };

      fetchVoucherData();
    }
  }, [isEditMode, voucherId]);

  // Predefined Itinerary Suggestions
  const [predefinedItineraries, setPredefinedItineraries] = useState([]);
  const [showAllPredefined, setShowAllPredefined] = useState(false);

  useEffect(() => {
    const fetchPredefined = async () => {
      try {
        const res = await predefineItineraryService.getItineraries();
        if (res.success && res.data) {
          setPredefinedItineraries(res.data.filter(it => it.status !== 'archived'));
        }
      } catch (err) {
        console.warn("Could not fetch predefined itineraries:", err);
      }
    };
    fetchPredefined();
  }, []);

  // Compute selected hotel & destination locations
  const selectedLocationIds = (formData.hotel_bookings || [])
    .map(h => h.location_id)
    .concat((formData.tour_info?.destinations || []).map(d => d.location_id))
    .filter(Boolean);

  const selectedCityNames = (formData.hotel_bookings || [])
    .map(h => (h.city || '').trim().toLowerCase())
    .concat((formData.tour_info?.destinations || []).map(d => (d.city || '').trim().toLowerCase()))
    .filter(Boolean);

  const matchingPredefinedItineraries = predefinedItineraries.filter(itin => {
    if (!itin) return false;
    const itinLocId = typeof itin.location === 'object' ? itin.location?._id : itin.location;
    const itinLocName = (typeof itin.location === 'object' ? itin.location?.name : (itin.location_name || itin.location || '')).toLowerCase();
    const itinTitle = (itin.title || itin.itinerary_title || '').toLowerCase();

    const matchesId = selectedLocationIds.some(id => id && id === itinLocId);
    const matchesCity = selectedCityNames.some(c => c && (itinLocName.includes(c) || c.includes(itinLocName) || itinTitle.includes(c)));

    return matchesId || matchesCity;
  });

  const applyPredefinedItinerary = (itin) => {
    if (!itin.days || itin.days.length === 0) {
      toast.error("This predefined itinerary has no days configured.");
      return;
    }

    const startDateStr = formData.tour_info?.start_date;

    const mappedDays = itin.days.map((dayItem, idx) => {
      const calculatedDate = calculateItineraryDate(startDateStr, idx) || (dayItem.date ? (formatToStandardDate(parseStandardDate(dayItem.date)) || dayItem.date) : '');

      let dayRemarks = [];
      if (dayItem.pointers && dayItem.pointers.length > 0) {
        dayRemarks = dayItem.pointers.filter(p => p && p.trim());
      }
      if (dayRemarks.length === 0 && dayItem.title) {
        dayRemarks = [dayItem.title];
      }
      if (dayRemarks.length === 0) {
        dayRemarks = [''];
      }

      return {
        id: Math.random().toString(36).substr(2, 9),
        day: dayItem.title || dayItem.day || `Day ${idx + 1}`,
        title: dayItem.title || dayItem.day || '',
        date: calculatedDate,
        remarks: dayRemarks,
        timings: dayRemarks.map(() => '')
      };
    });

    setFormData(prev => ({
      ...prev,
      itinerary: mappedDays
    }));

    const itinTitle = itin.title || itin.itinerary_title || 'Predefined Itinerary';
    toast.success(`Applied "${itinTitle}" to Trip Itinerary!`, { icon: '✨' });
  };

  useEffect(() => {
    if (!isEditMode) {
      voucherService.getLocations()
        .then(res => {
          const locations = res?.data || res || [];
          const sortedLocations = [...locations].sort((a, b) => a.name.localeCompare(b.name));
          setAllLocations(sortedLocations);
        })
        .catch(err => console.error("Error fetching locations:", err));
    }
  }, [isEditMode]);

  const pdfRef = useRef(null);
  const pdfContainerRef = useRef(null);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    type: 'info',
    showCancel: true,
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  const clearDraft = () => {
    localStorage.removeItem('trrip_voucher_draft');
    localStorage.removeItem('trrip_voucher_tab');
    localStorage.removeItem('trrip_voucher_booking_id');
  };

  const generateAvailableBookingId = async () => {
    if (isEditMode) return bookingId;
    for (let attempt = 0; attempt < 10; attempt++) {
      const candidate = Math.random().toString(36).substring(2, 7).toLowerCase();
      try {
        const res = await voucherService.checkBookingId(candidate);
        if (res?.available) {
          setBookingId(candidate);
          localStorage.setItem('trrip_voucher_booking_id', candidate);
          return candidate;
        }
      } catch (e) {
        setBookingId(candidate);
        localStorage.setItem('trrip_voucher_booking_id', candidate);
        return candidate;
      }
    }
    const fallbackId = Math.random().toString(36).substring(2, 7).toLowerCase();
    setBookingId(fallbackId);
    localStorage.setItem('trrip_voucher_booking_id', fallbackId);
    return fallbackId;
  };

  useEffect(() => {
    if (isEditMode) return;
    const initBookingId = async () => {
      if (!bookingId) {
        await generateAvailableBookingId();
      } else if (!initialData?.booking_id && !initialData?.bookingId) {
        try {
          const res = await voucherService.checkBookingId(bookingId);
          if (!res?.available) {
            await generateAvailableBookingId();
          }
        } catch (err) {
          console.error('Error verifying initial booking ID:', err);
        }
      }
    };
    initBookingId();
  }, [bookingId, initialData, isEditMode]);

  const lastSyncedData = useRef(null);

  useEffect(() => {
    lastSyncedData.current = JSON.stringify(formData);
  }, []);

  useEffect(() => {
    if (onDataChange) {
      const dataString = JSON.stringify(formData);
      if (lastSyncedData.current !== dataString) {
        lastSyncedData.current = dataString;
        onDataChange(formData);
      }
    }
  }, [formData, onDataChange]);

  const addContact = () => {
    const newId = `contact-${Date.now()}`;
    setFormData(prev => ({
      ...prev,
      contact_details: [
        ...(prev.contact_details || []),
        { id: newId, label: '', name: '', number: '', description: '', vehicle_type: '', vehicle_number: '' }
      ]
    }));
    setTimeout(() => {
      const element = document.getElementById(newId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  useEffect(() => {
    if (initialData) {
      const dataString = JSON.stringify(initialData);
      if (lastSyncedData.current !== dataString) {
        lastSyncedData.current = dataString;
        setFormData(prev => ({
          ...prev,
          ...initialData,
          tour_info: {
            ...prev.tour_info,
            ...initialData.tour_info,
            start_date: standardizeDate(initialData.tour_info?.start_date),
            end_date: standardizeDate(initialData.tour_info?.end_date)
          },
          hotel_bookings: (initialData.hotel_bookings || []).map(h => ({
            ...h,
            id: h.id || Math.random().toString(36).substr(2, 9),
            check_in_date: standardizeDate(h.check_in_date),
            check_in_time: standardizeTime(h.check_in_time) || '',
            check_out_date: standardizeDate(h.check_out_date),
            check_out_time: standardizeTime(h.check_out_time) || ''
          })),
          travel_bookings: (initialData.travel_bookings || []).map(t => ({
            ...t,
            id: t.id || Math.random().toString(36).substr(2, 9),
            journey_date: standardizeDate(t.journey_date),
            departure_time: standardizeTime(t.departure_time),
            arrival_time: standardizeTime(t.arrival_time)
          })),
          contact_details: initialData.contact_details || [],
          itinerary: initialData.itinerary || [],
          important_documents: (initialData.important_documents || []).map(d => ({ ...d, id: d.id || Math.random().toString(36).substr(2, 9) })),
          source_documents: initialData.source_documents || prev.source_documents || []
        }));
      }
    }

    // In create mode only, sync booking ID from AI analysis or draft if available
    if (!isEditMode && !initialData?.booking_id && !initialData?.bookingId) {
      const storedId = localStorage.getItem('trrip_voucher_booking_id');
      if (storedId && storedId !== bookingId) {
        setBookingId(storedId);
      }
    }
  }, [initialData, analysisState.result, isEditMode]);

  useEffect(() => {
    if (agent) {
      setAgentName(agent.company_name || '');
      setAgentEmail(agent.email || '');
      setAgentPhone(agent.phone_number || '');
      setAgentAddress(agent.address || '');
      setCity(agent.city || '');
      setPincode(agent.pincode || '');

      if (agent.logo) {
        const fetchLogo = async () => {
          try {
            const proxyUrl = getProxyUrl(agent.logo);
            const res = await fetch(proxyUrl);
            const blob = await res.blob();
            const reader = new FileReader();
            reader.onloadend = () => setLogo(reader.result);
            reader.readAsDataURL(blob);
          } catch (err) {
            setLogo(agent.logo);
          }
        };
        fetchLogo();
      }
    }
  }, [agent]);

  const generateWhatsAppMessage = useCallback(() => {
    let guestName = formData.guest_information?.guest_name || 'Guest';
    if (guestName.includes(',')) guestName = guestName.split(',')[0].trim();
    const destination = formData.tour_info?.destinations?.map(d => d.city).join(', ') || 'Your Destination';
    const firstDest = formData.tour_info?.destinations?.[0]?.city || 'your destination';
    const androidLink = `https://play.google.com/store/apps/details?id=live.trrip.app&hl=en_IN&bookingId=${bookingId}`;
    const iosLink = `https://apps.apple.com/in/app/trrip-your-ontour-assistant/id6759519854`;

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date)) return dateString;
      const options = { day: 'numeric', month: 'short', year: 'numeric' };
      return date.toLocaleDateString('en-GB', options);
    };

    const startDate = formData.tour_info?.start_date ? formatDate(formData.tour_info.start_date) : '';
    const endDate = formData.tour_info?.end_date ? formatDate(formData.tour_info.end_date) : '';
    const travelDates = (startDate && endDate) ? `${startDate} – ${endDate}` : (startDate || endDate || 'TBD');

    return `*${agentName || 'Travel Company'}*
Your travel companion *TRRIP app* for ${firstDest} is ready.

───────────────
*Traveller:* ${guestName}
*Destination:* ${destination}
*Travel dates:* ${travelDates}
*Login ID:* ${bookingId}
───────────────

Download the app and log in with your mobile number + password above.

▶ Play Store: ${androidLink}
App Store: ${iosLink}`;
  }, [formData, bookingId, agentName]);

  const handleInputChange = (path, value) => {
    const pathArray = path.split('.');
    setFormData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;
      for (let i = 0; i < pathArray.length - 1; i++) {
        current = current[pathArray[i]];
      }
      current[pathArray[pathArray.length - 1]] = value;

      if (path === 'tour_info.start_date') {
        if (value) {
          newData.itinerary = (newData.itinerary || []).map((day, idx) => ({
            ...day,
            date: calculateItineraryDate(value, idx) || day.date
          }));

          const parsedStart = parseStandardDate(value);
          const parsedEnd = parseStandardDate(newData.tour_info?.end_date);
          if (parsedStart && parsedEnd) {
            parsedStart.setHours(0, 0, 0, 0);
            parsedEnd.setHours(0, 0, 0, 0);
            if (parsedEnd <= parsedStart) {
              newData.tour_info.end_date = '';
            }
          }
        }
      }

      return newData;
    });
  };

  const updateHotel = (idx, field, value) => {
    setFormData(prev => {
      const newHotels = [...prev.hotel_bookings];
      newHotels[idx][field] = value;

      if (field === 'location_id') {
        const selectedLocation = allLocations.find(loc => loc._id === value);
        if (selectedLocation) {
          newHotels[idx]['city'] = selectedLocation.name;
        }
      }

      // Re-derive authoritative destinations from all valid hotels
      const derivedDests = [];
      const seenDests = new Set();
      newHotels.forEach(h => {
        let hLocId = h.location_id || null;
        let hCity = (h.city || '').trim();
        let hLocName = null;
        if (hLocId) {
          const loc = allLocations.find(l => l._id === hLocId);
          if (loc) {
            hLocName = loc.name;
            if (!hCity) hCity = loc.name;
          }
        }
        if (!hLocId && hCity) {
          const loc = allLocations.find(l => l.name?.toLowerCase().trim() === hCity.toLowerCase());
          if (loc) {
            hLocId = loc._id;
            hLocName = loc.name;
          }
        }
        const dedup = hLocId ? `loc_${hLocId}` : (hCity ? `city_${hCity.toLowerCase()}` : null);
        if (dedup && !seenDests.has(dedup)) {
          seenDests.add(dedup);
          derivedDests.push({
            city: hCity || hLocName || '',
            location_id: hLocId || '',
            location_name: hLocName || hCity || '',
            journey_date: h.check_in_date || ''
          });
        }
      });

      return {
        ...prev,
        hotel_bookings: newHotels,
        tour_info: {
          ...prev.tour_info,
          destinations: derivedDests.length > 0 ? derivedDests : (prev.tour_info?.destinations || [])
        }
      };
    });
  };

  const updateTravel = (idx, field, value) => {
    setFormData(prev => {
      const newTravel = [...prev.travel_bookings];
      newTravel[idx][field] = value;
      return { ...prev, travel_bookings: newTravel };
    });
  };

  const handleArrayFieldChange = (arrayPath, index, field, value) => {
    if (arrayPath === 'hotel_bookings') {
      updateHotel(index, field, value);
    } else if (arrayPath === 'travel_bookings') {
      updateTravel(index, field, value);
    } else {
      setFormData(prev => {
        const newData = JSON.parse(JSON.stringify(prev));
        newData[arrayPath][index][field] = value;
        return newData;
      });
    }
  };

  const addItineraryDay = () => {
    const dayIdx = (formData.itinerary?.length || 0);
    const nextDayNum = dayIdx + 1;
    const autoDate = calculateItineraryDate(formData.tour_info?.start_date, dayIdx);

    setFormData(prev => ({
      ...prev,
      itinerary_source: prev.itinerary_source || 'manual',
      itinerary: [
        ...(prev.itinerary || []),
        {
          id: Math.random().toString(36).substr(2, 9),
          day: `Day ${nextDayNum}`,
          title: "",
          day_title: "",
          date: autoDate || "",
          remarks: [""],
          timings: [""],
          events: [
            {
              id: `evt_${nextDayNum}_1_${Math.random().toString(36).substr(2, 5)}`,
              title: "",
              time: null,
              time_end: null,
              time_context: null,
              tag: "other",
              description: null
            }
          ]
        }
      ]
    }));
    setTimeout(() => {
      const idx = (formData.itinerary || []).length;
      const element = document.getElementById(`itin-${idx}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const addHotel = () => {
    setFormData(prev => ({
      ...prev,
      hotel_bookings: [...prev.hotel_bookings, { id: Math.random().toString(36).substr(2, 9), hotel_name: '', hotel_address: '', hotel_phone: '', map_link: '', city: '', location_id: '', check_in_date: '', check_in_time: '', check_out_date: '', check_out_time: '', confirmation_number: '', meal_plan: '', inclusions: '', no_of_rooms: '', room_category: '' }]
    }));
    setTimeout(() => {
      const idx = formData.hotel_bookings.length;
      const element = document.getElementById(`hotel-${idx}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-2', 'ring-emerald-500', 'ring-offset-2');
        setTimeout(() => element.classList.remove('ring-2', 'ring-emerald-500', 'ring-offset-2'), 2000);
      }
    }, 100);
  };

  const moveHotel = (idx, direction) => {
    setFormData(prev => {
      const newHotels = [...prev.hotel_bookings];
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;

      if (targetIdx < 0 || targetIdx >= newHotels.length) return prev;

      [newHotels[idx], newHotels[targetIdx]] = [newHotels[targetIdx], newHotels[idx]];

      const derivedDests = [];
      const seenDests = new Set();
      newHotels.forEach(h => {
        let hLocId = h.location_id || null;
        let hCity = (h.city || '').trim();
        let hLocName = null;
        if (hLocId) {
          const loc = allLocations.find(l => l._id === hLocId);
          if (loc) {
            hLocName = loc.name;
            if (!hCity) hCity = loc.name;
          }
        }
        if (!hLocId && hCity) {
          const loc = allLocations.find(l => l.name?.toLowerCase().trim() === hCity.toLowerCase());
          if (loc) {
            hLocId = loc._id;
            hLocName = loc.name;
          }
        }
        const dedup = hLocId ? `loc_${hLocId}` : (hCity ? `city_${hCity.toLowerCase()}` : null);
        if (dedup && !seenDests.has(dedup)) {
          seenDests.add(dedup);
          derivedDests.push({
            city: hCity || hLocName || '',
            location_id: hLocId || '',
            location_name: hLocName || hCity || '',
            journey_date: h.check_in_date || ''
          });
        }
      });

      return {
        ...prev,
        hotel_bookings: newHotels,
        tour_info: { ...prev.tour_info, destinations: derivedDests.length > 0 ? derivedDests : (prev.tour_info?.destinations || []) }
      };
    });
  };

  const addTravel = () => {
    setFormData(prev => ({
      ...prev,
      travel_bookings: [...prev.travel_bookings, {
        id: Math.random().toString(36).substr(2, 9),
        operator: '',
        mode_of_transport: '',
        from: '',
        to: '',
        departure_terminal: '',
        arrival_terminal: '',
        departure_time: '',
        arrival_time: '',
        journey_date: '',
        arrival_date: '',
        pnr: '',
        travel_class: '',
        vehicle_number: '',
        seat_no: '',
        passenger_name: '',
        driver_name: '',
        driver_contact: '',
        custom_fields: []
      }]
    }));
    setTimeout(() => {
      const idx = formData.travel_bookings.length;
      const element = document.getElementById(`travel-${idx}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
        setTimeout(() => element.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2'), 2000);
      }
    }, 100);
  };

  const moveTravel = (idx, direction) => {
    setFormData(prev => {
      const newTravel = [...prev.travel_bookings];
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;

      if (targetIdx < 0 || targetIdx >= newTravel.length) return prev;

      [newTravel[idx], newTravel[targetIdx]] = [newTravel[targetIdx], newTravel[idx]];
      return { ...prev, travel_bookings: newTravel };
    });
  };

  const addImportantDocument = () => {
    setFormData(prev => ({
      ...prev,
      important_documents: [
        ...(prev.important_documents || []),
        { id: Math.random().toString(36).substr(2, 9), name: '', url: '', filename: '', isUploading: false }
      ]
    }));
  };

  const updateImportantDocumentName = (idx, name) => {
    setFormData(prev => {
      const newDocs = [...(prev.important_documents || [])];
      newDocs[idx] = { ...newDocs[idx], name };
      return { ...prev, important_documents: newDocs };
    });
  };

  const handleImportantDocUpload = async (idx, file) => {
    if (!file) return;
    setFormData(prev => {
      const newDocs = [...(prev.important_documents || [])];
      newDocs[idx] = { ...newDocs[idx], isUploading: true };
      return { ...prev, important_documents: newDocs };
    });

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const res = await voucherService.uploadSingle(uploadFormData);
      const uploadedUrl = res?.fileUrl || res?.url || res?.data?.url;
      setFormData(prev => {
        const newDocs = [...(prev.important_documents || [])];
        newDocs[idx] = {
          ...newDocs[idx],
          url: uploadedUrl,
          filename: file.name,
          isUploading: false
        };
        return { ...prev, important_documents: newDocs };
      });
      toast.success('Document uploaded successfully!');
    } catch (err) {
      console.error('Failed to upload document:', err);
      toast.error('Failed to upload document.');
      setFormData(prev => {
        const newDocs = [...(prev.important_documents || [])];
        newDocs[idx] = { ...newDocs[idx], isUploading: false };
        return { ...prev, important_documents: newDocs };
      });
    }
  };

  const removeImportantDocument = (idx) => {
    setFormData(prev => ({
      ...prev,
      important_documents: (prev.important_documents || []).filter((_, i) => i !== idx)
    }));
  };

  const executeSaveVoucher = async (action = 'download') => {
    if (isSaved) {
      if (action === 'share') {
        setShowShareDropdown(true);
        return;
      }
    }

    if (isSubmittingRef.current) return;

    if (isVoucherLocked) {
      navigate('/subscription');
      toast.error('Active subscription or trial required to create vouchers. Upgrade to unlock.');
      return;
    }

    const scrollToField = (id, message) => {
      const element = document.getElementById(id);
      if (element) {
        if (typeof element.scrollIntoView === 'function') {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        element.classList.add('ring-2', 'ring-red-500', 'ring-offset-2');
        setTimeout(() => element.classList.remove('ring-2', 'ring-red-500', 'ring-offset-2'), 3000);
      }
      toast.error(message);
      return;
    };

    let currentBookingId = bookingId;
    if (!currentBookingId && !isEditMode) {
      currentBookingId = await generateAvailableBookingId();
    }
    if (!currentBookingId) {
      return scrollToField('guest-info-section', "Voucher ID is required.");
    }
    if (!/^[a-z0-9]{5}$/.test(currentBookingId.toLowerCase())) {
      return scrollToField('guest-info-section', "Voucher ID must be exactly 5 lowercase alphanumeric characters.");
    }
    try {
      const targetDbId = loadedVoucherDbId || voucherId;
      const checkUrl = isEditMode && targetDbId
        ? `${currentBookingId}?excludeVoucherId=${targetDbId}`
        : currentBookingId;
      const res = await voucherService.checkBookingId(checkUrl);
      if (!res?.available) {
        if (!isEditMode && !initialData?.booking_id && !initialData?.bookingId) {
          currentBookingId = await generateAvailableBookingId();
        } else {
          return scrollToField('guest-info-section', 'Voucher ID is already in use.');
        }
      }
    } catch (err) {
      console.error(err);
    }

    if (!formData.guest_information?.guest_name?.trim()) {
      return scrollToField('guest-info-section', "Guest Name is required.");
    }

    if (!formData.tour_info?.start_date) {
      return scrollToField('guest-info-section', "Trip Start Date is required.");
    }

    if (!formData.tour_info?.end_date) {
      return scrollToField('guest-info-section', "Trip End Date is required.");
    }

    const startDate = new Date(formData.tour_info.start_date);
    const endDate = new Date(formData.tour_info.end_date);
    if (startDate > endDate) {
      return scrollToField('guest-info-section', "Start date must be before End date.");
    }

    if (!formData.hotel_bookings || formData.hotel_bookings.length === 0) {
      return scrollToField('hotel-bookings-section', "At least 1 hotel booking is mandatory.");
    }
    for (let i = 0; i < formData.hotel_bookings.length; i++) {
      const hotel = formData.hotel_bookings[i];
      const hId = hotel._id || hotel.id || i;
      if (!hotel.hotel_name?.trim()) return scrollToField(`hotel-${hId}`, `Hotel Name is required for Hotel #${i + 1}`);
      if (!hotel.city?.trim() && !hotel.location_id) return scrollToField(`hotel-${hId}`, `City / Destination is required for Hotel #${i + 1}`);
      if (!hotel.check_in_date) return scrollToField(`hotel-${hId}`, `Check-in Date is required for Hotel #${i + 1}`);
      if (!hotel.check_out_date) return scrollToField(`hotel-${hId}`, `Check-out Date is required for Hotel #${i + 1}`);
      if (!hotel.no_of_rooms) return scrollToField(`hotel-${hId}`, `No. of Rooms is required for Hotel #${i + 1}`);
      if (!hotel.meal_plan) return scrollToField(`hotel-${hId}`, `Meal Plan is required for Hotel #${i + 1}`);
    }

    if (formData.travel_bookings && formData.travel_bookings.length > 0) {
      for (let i = 0; i < formData.travel_bookings.length; i++) {
        const travel = formData.travel_bookings[i];
        if (!travel.operator || !travel.mode_of_transport || !travel.from || !travel.to || !travel.journey_date) {
          return scrollToField(`travel-${i}`, `Please fill all required fields for Travel #${i + 1}`);
        }
      }
    }

    isSubmittingRef.current = true;
    setIsDownloading(true);
    const toastId = toast.loading(isEditMode ? "Updating voucher..." : "Generating voucher...");

    try {
      const MARGIN = 28;
      const container = pdfRef.current;

      const fullCanvas = await html2canvas(container, {
        scale: 2.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const pdf = new jsPDF("p", "pt", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const contentW = pageW - MARGIN * 2;
      const contentH = pageH - MARGIN * 2;

      const canvasToPagePt = contentW / fullCanvas.width;
      const pageHeightPx = contentH / canvasToPagePt;
      const containerTop = container.getBoundingClientRect().top;

      const lastPageEl = container.querySelector('[data-pdf-section="last-page"]');
      let lastPageTop = fullCanvas.height;
      if (lastPageEl) {
        const calculatedTop = Math.round((lastPageEl.getBoundingClientRect().top - containerTop) * 2.5);
        if (calculatedTop > 100 && calculatedTop < fullCanvas.height) {
          lastPageTop = calculatedTop;
        }
      }

      const mainContentEnd = lastPageEl && lastPageTop < fullCanvas.height ? lastPageTop : fullCanvas.height;

      const sections = container.querySelectorAll('[data-pdf-section]');
      const sectionBreaks = [0];
      sections.forEach(sec => {
        if (sec === lastPageEl) return;
        const relTop = Math.round((sec.getBoundingClientRect().top - containerTop) * 2.5);
        if (relTop > 0 && relTop < mainContentEnd) sectionBreaks.push(relTop);
      });
      sectionBreaks.push(mainContentEnd);

      const pageSlices = [];
      let pageStart = 0;

      while (pageStart < mainContentEnd) {
        const idealEnd = pageStart + pageHeightPx;

        if (idealEnd >= mainContentEnd) {
          pageSlices.push({ start: pageStart, end: mainContentEnd });
          break;
        }

        let bestBreak = idealEnd;
        for (let i = sectionBreaks.length - 1; i >= 0; i--) {
          if (sectionBreaks[i] <= idealEnd && sectionBreaks[i] > pageStart && sectionBreaks[i] < mainContentEnd) {
            bestBreak = sectionBreaks[i];
            break;
          }
        }

        if (bestBreak <= pageStart) {
          bestBreak = idealEnd;
        }

        pageSlices.push({ start: pageStart, end: bestBreak });
        pageStart = bestBreak;
      }

      if (lastPageEl && lastPageTop < fullCanvas.height) {
        pageSlices.push({ start: lastPageTop, end: fullCanvas.height, isLastPage: true });
      }

      pageSlices.forEach((slice, idx) => {
        if (idx > 0) pdf.addPage();

        const sliceH = slice.end - slice.start;
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = fullCanvas.width;
        sliceCanvas.height = sliceH;
        const ctx = sliceCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(fullCanvas, 0, -slice.start);

        let sliceHPt = sliceH * canvasToPagePt;
        let drawW = contentW;
        let drawH = sliceHPt;
        let offsetX = MARGIN;
        let offsetY = MARGIN;

        if (slice.isLastPage || (lastPageEl && idx === pageSlices.length - 1)) {
          if (sliceHPt > contentH) {
            const scale = contentH / sliceHPt;
            drawW = contentW * scale;
            drawH = contentH;
            offsetX = MARGIN + (contentW - drawW) / 2;
          }
        }

        pdf.addImage(sliceCanvas.toDataURL('image/jpeg', 1.0), 'JPEG', offsetX, offsetY, drawW, drawH);

        if (slice.isLastPage || (lastPageEl && idx === pageSlices.length - 1)) {
          const targetBookingId = currentBookingId || bookingId || 'a1b2c';
          const playStoreUrl = `https://play.google.com/store/apps/details?id=live.trrip.app&hl=en_IN&bookingId=${targetBookingId}`;
          const appStoreUrl = `https://apps.apple.com/us/app/trrip-your-ontour-assistant/id6759519854`;

          pdf.link(
            offsetX + drawW * 0.052,
            offsetY + drawH * 0.745,
            drawW * 0.220,
            drawH * 0.055,
            { url: playStoreUrl }
          );

          pdf.link(
            offsetX + drawW * 0.052,
            offsetY + drawH * 0.805,
            drawW * 0.220,
            drawH * 0.055,
            { url: appStoreUrl }
          );
        }
      });

      const fileName = `${formData.guest_information.guest_name}_Voucher.pdf`;
      const pdfBlob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);
      setGeneratedPdfUrl(blobUrl);

      const cleanedFormData = JSON.parse(JSON.stringify(formData));
      if (cleanedFormData.hotel_bookings) {
        cleanedFormData.hotel_bookings = cleanedFormData.hotel_bookings.map(({ id, ...rest }) => ({
          ...rest,
          location_id: rest.location_id === "" ? null : rest.location_id
        }));

        // Authoritatively derive tour_info.destinations from hotel bookings
        const validHotels = cleanedFormData.hotel_bookings.filter(h =>
          h && ((h.city && String(h.city).trim()) || h.location_id || (h.hotel_name && String(h.hotel_name).trim()))
        );

        if (validHotels.length > 0) {
          const derivedDestinations = [];
          const seen = new Set();
          for (const hotel of validHotels) {
            let locId = hotel.location_id?._id || hotel.location_id || null;
            if (locId === "" || locId === "null" || locId === "undefined") locId = null;
            let cityName = (hotel.city || '').trim();
            let locationName = hotel.location_name || null;

            if (allLocations && allLocations.length > 0) {
              if (!locId && cityName) {
                const match = allLocations.find(l => l.name?.toLowerCase().trim() === cityName.toLowerCase().trim());
                if (match) {
                  locId = match._id;
                  locationName = match.name;
                  if (!cityName) cityName = match.name;
                }
              }
              if (locId && !locationName) {
                const match = allLocations.find(l => l._id?.toString() === locId.toString());
                if (match) {
                  locationName = match.name;
                  if (!cityName) cityName = match.name;
                }
              }
            }

            if (!cityName && locationName) cityName = locationName;
            if (!cityName && !locId) continue;

            const dedupKey = locId ? `loc_${locId.toString()}` : `city_${cityName.toLowerCase().trim()}`;
            if (!seen.has(dedupKey)) {
              seen.add(dedupKey);
              derivedDestinations.push({
                city: cityName || locationName || '',
                location_id: locId || null,
                location_name: locationName || cityName || null,
                journey_date: hotel.check_in_date || null
              });
            }
          }

          if (derivedDestinations.length > 0) {
            if (!cleanedFormData.tour_info) cleanedFormData.tour_info = {};
            cleanedFormData.tour_info.destinations = derivedDestinations;
          }
        }
      }

      if (cleanedFormData.tour_info && cleanedFormData.tour_info.destinations) {
        cleanedFormData.tour_info.destinations = cleanedFormData.tour_info.destinations.map(dest => ({
          ...dest,
          location_id: dest.location_id === "" ? null : dest.location_id
        }));
      }
      if (cleanedFormData.travel_bookings) {
        cleanedFormData.travel_bookings = cleanedFormData.travel_bookings.map(({ id, ...rest }) => rest);
      }
      if (cleanedFormData.itinerary) {
        cleanedFormData.itinerary = cleanedFormData.itinerary.map(({ id, ...rest }) => ({
          ...rest,
          remarks: rest.remarks || [],
          timings: rest.timings || []
        }));
      }
      if (cleanedFormData.contact_details) {
        cleanedFormData.contact_details = cleanedFormData.contact_details.map(({ id, ...rest }) => rest);
      }
      if (cleanedFormData.important_documents) {
        cleanedFormData.important_documents = cleanedFormData.important_documents
          .filter(doc => (doc.name && doc.name.trim()) || doc.url)
          .map(({ id, isUploading, ...rest }) => rest);
      }
      if (cleanedFormData.source_documents) {
        cleanedFormData.source_documents = cleanedFormData.source_documents
          .filter(doc => (doc.name && doc.name.trim()) || doc.url || doc.filename)
          .map(({ id, isUploading, ...rest }) => rest);
      }

      const previousDraftId = !isEditMode ? localStorage.getItem('trrip_voucher_booking_id') : null;
      const targetDbId = loadedVoucherDbId || voucherId;
      const finalBookingId = (currentBookingId || bookingId || '').toLowerCase();

      const submitData = new FormData();
      submitData.append('pdfFile', pdfBlob, fileName);
      submitData.append('voucherData', JSON.stringify({
        booking_id: finalBookingId,
        ...(previousDraftId && { previous_draft_id: previousDraftId }),
        ...cleanedFormData,
        itenary_link: fileName,
        voucher_theme: {
          is_custom: Boolean(formVoucherTheme?.is_custom || formVoucherTheme?.isCustom),
          primary_color: formVoucherTheme?.primary_color || formVoucherTheme?.primaryColor || '#0d9488',
          secondary_color: formVoucherTheme?.secondary_color || formVoucherTheme?.secondaryColor || '#1d4ed8',
          text_color: formVoucherTheme?.text_color || formVoucherTheme?.textColor || '#111827',
          header_bg_color: formVoucherTheme?.header_bg_color || formVoucherTheme?.headerBgColor || '#ffffff',
          include_itinerary: Boolean(formVoucherTheme?.include_itinerary)
        }
      }));

      if (isEditMode) {
        await voucherService.updateVoucher(targetDbId, submitData, true);
      } else {
        await voucherService.createVoucher(submitData);
        clearDraft();
      }

      if (typeof refreshSubscription === 'function') {
        refreshSubscription();
      }

      toast.dismiss(toastId);
      setIsSaved(true);
      if (typeof onSaved === 'function') {
        onSaved();
      }

      if (isEditMode) {
        if (action === 'download') {
          pdf.save(fileName);
          toast.success(isReviewMode ? "Voucher saved & downloaded!" : "Voucher updated & downloaded!");
          navigate('/voucher');
        } else if (action === 'share') {
          setShowShareDropdown(true);
          toast.success(isReviewMode ? "Voucher saved and sharing options ready!" : "Voucher updated and sharing options ready!");
        } else {
          toast.success(isReviewMode ? "Voucher saved successfully!" : "Voucher updated successfully!");
          navigate('/voucher');
        }
      } else {
        if (action === 'download') {
          pdf.save(fileName);
          toast.success("Voucher saved & downloaded!");
          navigate('/voucher');
          if (setActiveTab) setActiveTab('voucher');
        } else if (action === 'share') {
          setShowShareDropdown(true);
          toast.success("Voucher saved! Sharing options ready.");
        } else if (action === 'only-save') {
          toast.success("Voucher saved successfully!");
          navigate('/voucher');
          if (setActiveTab) setActiveTab('voucher');
        } else {
          toast.success("Voucher saved!");
          navigate('/voucher');
          if (setActiveTab) setActiveTab('voucher');
        }
      }

    } catch (error) {
      toast.dismiss(toastId);
      if (error.response?.status === 429) {
        setShowLimitPopup(true);
        toast.error(error.response?.data?.message || "Rate limit reached: Maximum 10 vouchers allowed in 30 minutes.");
      } else if (error.response?.status === 403 && error.response?.data?.error === 'SUBSCRIPTION_REQUIRED') {
        toast.error("Active subscription or trial required to create vouchers. Please upgrade your plan.");
        navigate('/subscription');
      } else {
        toast.error(error.response?.data?.message || (isEditMode ? "Failed to update voucher." : "Failed to save voucher."));
      }
    } finally {
      isSubmittingRef.current = false;
      setIsDownloading(false);
    }
  };

  const closeConfirmation = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  if (isEditMode && loadError) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center p-6">
        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mb-4">
          <AlertCircle size={32} />
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Unable to open voucher</h2>
        <p className="text-sm text-slate-500 max-w-md mb-6">
          {loadError}
        </p>
        <button
          type="button"
          onClick={() => navigate('/voucher')}
          className="px-5 py-2.5 bg-[#067978] text-white rounded-xl font-semibold text-sm hover:bg-[#056362] transition-colors shadow-sm cursor-pointer"
        >
          Return to Vouchers
        </button>
      </div>
    );
  }

  if (isEditMode && isLoadingData) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-10 h-10 border-4 border-[#067978] border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-slate-500 font-medium tracking-widest text-[10px] uppercase">Loading voucher details...</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col h-full bg-slate-50 relative pb-12">
        {/* Edit / Review Voucher Header with dedicated actions */}
        {(isEditMode || isReviewMode) && (
          <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="w-full xl:w-auto">
              <h1 className="text-xl sm:text-2xl font-black text-[#067978] tracking-tight uppercase">
                {isReviewMode ? 'Review Voucher' : 'Edit Voucher'}
              </h1>
              <div className="flex items-center gap-2 mt-1 text-xs sm:text-sm text-slate-500 font-medium">
                <span>ID: <strong className="text-slate-800 font-bold">{bookingId || (isEditMode ? '—' : '')}</strong></span>
                {bookingId && (
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(bookingId);
                      toast.success(`Voucher ID (${bookingId}) copied!`);
                    }}
                    className="p-1 text-slate-400 hover:text-[#067978] hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                    title="Copy Voucher ID"
                  >
                    <Copy size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/voucher')}
                className="flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold text-sm shadow-sm hover:bg-slate-50 transition-all cursor-pointer"
              >
                <ChevronLeft size={16} />
                <span>Back</span>
              </button>
              <button
                type="button"
                onClick={() => setIsEnrichModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-[#067978] rounded-xl font-bold text-sm shadow-sm hover:bg-slate-50 transition-all active:scale-95 cursor-pointer"
              >
                <FileText size={16} className="text-[#067978]" />
                <span>Add Documents</span>
              </button>
              <button
                type="button"
                onClick={() => setIsPreviewModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold text-sm shadow-sm hover:bg-slate-50 transition-all cursor-pointer"
              >
                <Eye size={16} />
                <span>Preview</span>
              </button>
              <button
                type="button"
                onClick={executeSaveVoucher}
                disabled={isDownloading || isVoucherLocked}
                className="flex items-center gap-2 px-6 py-2 bg-[#067978] text-white rounded-xl font-bold text-sm shadow-md hover:bg-[#056362] transition-all active:scale-95 disabled:opacity-50 cursor-pointer"
              >
                {isDownloading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <CheckCircle size={16} className="text-white" />
                )}
                <span>Save Voucher</span>
              </button>
            </div>
          </div>
        )}

        <div className="w-full space-y-8">

          {/* Document Enrichment & Quality Alerts (Create Mode only) */}
          {!isEditMode && !isReviewMode && (
            <EnrichmentHeader
              formData={formData}
              setIsEnrichModalOpen={setIsEnrichModalOpen}
              isVoucherLocked={isVoucherLocked}
              limitReached={limitReached}
              navigate={navigate}
            />
          )}

          {/* Guest Information */}
          <GuestInfoSection
            formData={formData}
            handleInputChange={handleInputChange}
            bookingId={bookingId}
            parseStandardDate={parseStandardDate}
          />

          {/* Hotel Bookings */}
          <HotelBookingsSection
            formData={formData}
            setFormData={setFormData}
            addHotel={addHotel}
            moveHotel={moveHotel}
            handleArrayFieldChange={handleArrayFieldChange}
            allLocations={allLocations}
          />

          {/* Travel Bookings */}
          <TravelBookingsSection
            formData={formData}
            setFormData={setFormData}
            addTravel={addTravel}
            moveTravel={moveTravel}
            handleArrayFieldChange={handleArrayFieldChange}
          />

          {/* Trip Itinerary */}
          <ItinerarySection
            formData={formData}
            setFormData={setFormData}
            formVoucherTheme={formVoucherTheme}
            setFormVoucherTheme={setFormVoucherTheme}
            addItineraryDay={addItineraryDay}
            predefinedItineraries={predefinedItineraries}
            showAllPredefined={showAllPredefined}
            setShowAllPredefined={setShowAllPredefined}
            matchingPredefinedItineraries={matchingPredefinedItineraries}
            applyPredefinedItinerary={applyPredefinedItinerary}
            selectedCityNames={selectedCityNames}
          />

          {/* Contact Details (Edit / Review Mode only) */}
          {(isEditMode || isReviewMode) && (
            <ContactDetailsSection
              formData={formData}
              setFormData={setFormData}
              addContact={addContact}
            />
          )}

          {/* Important Documents */}
          <ImportantDocsSection
            formData={formData}
            addImportantDocument={addImportantDocument}
            updateImportantDocumentName={updateImportantDocumentName}
            handleImportantDocUpload={handleImportantDocUpload}
            removeImportantDocument={removeImportantDocument}
          />
        </div>

        {/* Partner Visibility Settings for Traveller App */}
        <PartnerVisibilitySection
          formData={formData}
          handleInputChange={handleInputChange}
          selectiveExpanded={selectiveExpanded}
          setSelectiveExpanded={setSelectiveExpanded}
        />

        {/* Next-Gen Client Sharing Panel */}
        <ClientSharingSection
          formData={formData}
          bookingId={bookingId}
          formVoucherTheme={formVoucherTheme}
          isDownloading={isDownloading}
          isVoucherLocked={isVoucherLocked}
          limitReached={limitReached}
          isSaved={isSaved}
          showShareDropdown={showShareDropdown}
          setShowShareDropdown={setShowShareDropdown}
          generatedPdfUrl={generatedPdfUrl}
          executeSaveVoucher={executeSaveVoucher}
          generateWhatsAppMessage={generateWhatsAppMessage}
          clearDraft={clearDraft}
          navigate={navigate}
          setActiveTab={setActiveTab}
          setIsPreviewModalOpen={setIsPreviewModalOpen}
          isEditMode={isEditMode}
          isReviewMode={isReviewMode}
          voucherId={loadedVoucherDbId || voucherId}
        />

        {/* Hidden PDF Generator */}
        <div ref={pdfContainerRef} style={{ position: 'absolute', top: 0, left: '-9999px', width: '794px' }}>
          <div ref={pdfRef}>
            <PDFVoucherView
              data={formData}
              bookingId={bookingId}
              logo={logo}
              agentName={agentName}
              agentEmail={agentEmail}
              agentPhone={agentPhone}
              agentAddress={agentAddress}
              city={city}
              pincode={pincode}
              customTheme={formVoucherTheme}
            />
          </div>
        </div>
      </div>

      <EnrichVoucherModal
        isOpen={isEnrichModalOpen}
        onClose={() => setIsEnrichModalOpen(false)}
        existingData={formData}
      />

      <AiAnalysisProgress
        analysisState={analysisState}
        setAnalysisMinimized={setAnalysisMinimized}
        resetGlobalAnalysis={resetAnalysis}
      />

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onClose={closeConfirmation}
        type={confirmModal.type}
      />

      {isPreviewModalOpen && createPortal(
        <div className="fixed inset-0 z-[100000] bg-gray-950 flex flex-col animate-in fade-in duration-300">
          <button
            onClick={() => setIsPreviewModalOpen(false)}
            className="fixed top-8 right-8 z-[100001] p-4 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-all cursor-pointer"
          >
            <X size={32} />
          </button>
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 pt-24 sm:pt-12 flex justify-center items-start">
            <div className="shadow-2xl bg-white w-[794px] origin-top scale-[0.45] sm:scale-100 transition-transform duration-300 mb-[-450px] sm:mb-12">
              <PDFVoucherView
                data={formData}
                bookingId={bookingId}
                logo={logo}
                agentName={agentName}
                agentEmail={agentEmail}
                agentPhone={agentPhone}
                agentAddress={agentAddress}
                city={city}
                pincode={pincode}
                customTheme={formVoucherTheme}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {showLimitPopup && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={() => setShowLimitPopup(false)}></div>
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full relative z-10 shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-50 text-red-500 mb-6 border border-red-100">
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-2xl font-black text-gray-900 text-center mb-3">Rate Limit Reached</h3>
            <p className="text-slate-600 text-center mb-6 leading-relaxed font-medium">
              You can create a maximum of 10 vouchers every 30 minutes. Please wait a few minutes before saving another voucher.
            </p>
            <button
              onClick={() => setShowLimitPopup(false)}
              className="w-full bg-slate-900 text-white font-bold py-3.5 px-4 rounded-xl hover:bg-slate-800 hover:-translate-y-0.5 transition-all shadow-lg shadow-slate-900/20 active:translate-y-0 cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ManualVoucherForm;
